import { contentFetch } from "../contentClient.js";
import {
    resolveScopedIdArgs,
    resolveScopedLinkArgs,
    resolveScopedLinkPayloadArgs,
    resolveScopedListArgs,
    resolveScopedMutationArgs,
    resolveScopedPayloadArgs,
    resolveScopedRenameArgs,
    resolveScopedValueArgs,
} from "./scope.js";

// ============= Recipes API (v2) =============

const ENTITY_TYPE = "RECIPE";
const base = (projectId, versionId) =>
    `/api/v2/projects/${projectId}/versions/${versionId}/entities`;

export async function getRecipes({ projectId, versionId, search, page = 1, pageSize } = {}, init) {
    ({ args: { projectId, versionId, search, page, pageSize }, init } = resolveScopedListArgs({ projectId, versionId, search, page, pageSize }, init));
    const params = { page, "filter.entityType": ENTITY_TYPE };
    if (pageSize) params.pageSize = pageSize;
    if (search?.trim()) params["filter.systemName:contains"] = search.trim();
    return contentFetch(base(projectId, versionId), { params, ...(init || {}) });
}

export async function getRecipeById(projectId, versionId, id, init) {
    ({ projectId, versionId, id, init } = resolveScopedIdArgs(projectId, versionId, id, init));
    return contentFetch(`${base(projectId, versionId)}/${id}`, init);
}

export async function createRecipe(projectId, versionId, payload, init) {
    ({ projectId, versionId, payload, init } = resolveScopedPayloadArgs(projectId, versionId, payload, init));
    // payload: { systemName, name, description? }
    return contentFetch(base(projectId, versionId), {
        method: "POST",
        body: { ...payload, entityType: ENTITY_TYPE },
        ...(init || {}),
    });
}

export async function patchRecipe(projectId, versionId, id, payload, init) {
    ({ projectId, versionId, id, payload, init } = resolveScopedMutationArgs(projectId, versionId, id, payload, init));
    // payload: { nameRefId?, descriptionRefId?, name?, description? }
    return contentFetch(`${base(projectId, versionId)}/${id}`, {
        method: "PATCH",
        body: payload,
        ...(init || {}),
    });
}

export async function renameRecipeSystemName(projectId, versionId, id, newSystemName, init) {
    ({ projectId, versionId, id, newSystemName, init } = resolveScopedRenameArgs(projectId, versionId, id, newSystemName, init));
    return contentFetch(`${base(projectId, versionId)}/${id}:rename`, {
        method: "POST",
        body: { newSystemName },
        ...(init || {}),
    });
}

export async function deleteRecipe(projectId, versionId, id, init) {
    ({ projectId, versionId, id, init } = resolveScopedIdArgs(projectId, versionId, id, init));
    return contentFetch(`${base(projectId, versionId)}/${id}`, {
        method: "DELETE",
        ...(init || {}),
    });
}

// --- Parameter groups (replaces ingredient/result relationships in v2) ---

export async function getRecipeParameterGroups(projectId, versionId, id, init) {
    ({ projectId, versionId, id, init } = resolveScopedIdArgs(projectId, versionId, id, init));
    return contentFetch(`${base(projectId, versionId)}/${id}/parameter-groups`, init);
}

export async function attachParameterGroupToRecipe(projectId, versionId, id, payload, init) {
    ({ projectId, versionId, id, payload, init } = resolveScopedMutationArgs(projectId, versionId, id, payload, init));
    // payload: { parameterGroupId, orderIndex? }
    return contentFetch(`${base(projectId, versionId)}/${id}/parameter-groups`, {
        method: "POST",
        body: payload,
        ...(init || {}),
    });
}

export async function patchRecipeParameterGroupLink(projectId, versionId, id, linkId, payload, init) {
    ({ projectId, versionId, id, linkId, payload, init } = resolveScopedLinkPayloadArgs(projectId, versionId, id, linkId, payload, init));
    return contentFetch(`${base(projectId, versionId)}/${id}/parameter-groups/${linkId}`, {
        method: "PATCH",
        body: payload,
        ...(init || {}),
    });
}

export async function detachParameterGroupFromRecipe(projectId, versionId, id, linkId, init) {
    ({ projectId, versionId, id, linkId, init } = resolveScopedLinkArgs(projectId, versionId, id, linkId, init));
    return contentFetch(`${base(projectId, versionId)}/${id}/parameter-groups/${linkId}`, {
        method: "DELETE",
        ...(init || {}),
    });
}

// --- Values ---

export async function getRecipeValues(projectId, versionId, id, init) {
    ({ projectId, versionId, id, init } = resolveScopedIdArgs(projectId, versionId, id, init));
    return contentFetch(`${base(projectId, versionId)}/${id}/values`, init);
}

export async function setRecipeValue(projectId, versionId, id, parameterId, payload, init) {
    ({ projectId, versionId, id, parameterId, payload, init } = resolveScopedValueArgs(projectId, versionId, id, parameterId, payload, init));
    return contentFetch(`${base(projectId, versionId)}/${id}/values/${parameterId}`, {
        method: "PUT",
        body: payload,
        ...(init || {}),
    });
}

export async function deleteRecipeValue(projectId, versionId, id, parameterId, init) {
    ({ projectId, versionId, id, parameterId, init } = resolveScopedValueArgs(projectId, versionId, id, parameterId, undefined, init));
    return contentFetch(`${base(projectId, versionId)}/${id}/values/${parameterId}`, {
        method: "DELETE",
        ...(init || {}),
    });
}
